import React, { useState, useRef, useEffect } from "react";
import styled from "@emotion/styled";
import { motion } from "framer-motion";
import { useFieldArray, useFormContext } from "react-hook-form";
import Image from "@components/item/Image";
import { useTheme } from "@emotion/react";
import ReactDOM from "react-dom";
import useSelectedColorStore from "@store/register/useSelectedColor";
import { useQuery } from "@tanstack/react-query";
import { useApiRequest } from "@utils/ApiRequest";
import { useTranslation } from "react-i18next";
import { t } from "i18next";

const Container = styled.div({
  position: "relative",
  zIndex: 1,
});

const Button = styled.button<{ selectedValue: string }>(
  ({ theme, selectedValue }) => ({
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    gap: "0.4rem",
    width: "100%",
    fontSize: "1.6rem",
    fontWeight: theme.fontWeight.regular,
    lineHeight: "2rem",
    padding: "1.6rem",
    border: `1px solid ${theme.color.light_gray}`,
    color:
      selectedValue === t("선택") || selectedValue === "Select"
        ? theme.color.light_gray
        : theme.color.black,
  }),
);

const SelectBox = styled(motion.div)<{
  top: number;
  left: number;
  width: number;
}>(({ theme, top, left, width }) => ({
  display: "flex",
  flexDirection: "column",
  alignItems: "flex-start",
  position: "absolute",
  top: `${top}px`,
  left: `${left}px`,
  width: `${width}px`,
  backgroundColor: theme.color.white,
  border: `1px solid ${theme.color.light_gray}`,
  overflowY: "auto",
  maxHeight: "256px",
  zIndex: 10000,
}));

const OptionItem = styled.button<{ last: boolean }>(({ theme, last }) => ({
  width: "100%",
  padding: "10px 30px 10px 20px",
  display: "flex",
  gap: "1rem",
  alignItems: "center",
  fontSize: "1.6rem",
  lineHeight: "2.4rem",
  fontWeight: theme.fontWeight.regular,
  color: theme.color.black,
  backgroundColor: theme.color.white,
  border: "none",
  textAlign: "start",
  borderBottom: last ? "none" : `1px solid ${theme.color.border_gray}`,
  ":hover": {
    backgroundColor: theme.color.border_gray,
  },
}));

const ColorBall = styled.div<{ color: string }>(({ color }) => ({
  width: "1.6rem",
  height: "1.6rem",
  borderRadius: "50%",
  backgroundColor: color,
  transition: "background-color 0.3s ease",
}));

type Props = {
  name: string;
  open: boolean;
  setOpen: (value: boolean) => void;
  selectedValue: string;
  setSelectedValue: React.Dispatch<React.SetStateAction<string>>;
};
const ColorDropdown = ({
  name,
  open,
  setOpen,
  selectedValue,
  setSelectedValue,
}: Props) => {
  const { getColorList } = useApiRequest();
  const { t } = useTranslation();
  const { data: colorList } = useQuery({
    queryKey: ["colorList"],
    queryFn: getColorList,
  });

  const [focusName, setFocusName] = useState("");

  const { setValue, control } = useFormContext();
  const buttonRef = useRef<HTMLButtonElement>(null);
  const [dropdownPos, setDropdownPos] = useState({ top: 0, left: 0, width: 0 });
  const theme = useTheme();

  const { color, setColor, setColorId } = useSelectedColorStore();

  const { fields, append, remove } = useFieldArray({
    control,
    name,
  });

  const handleToggle = (e: React.MouseEvent) => {
    e.stopPropagation();
    setFocusName(name);
    if (colorList?.length === 0) return;

    setOpen(!open);
  };

  const handleChange = (
    colorName: string,
    colorValue: string,
    colorId: number,
  ) => {
    setOpen(false);
    setSelectedValue(colorName);
    setColor(colorName);
    setColorId(colorId);

    const existingColor = fields.find(
      (field: any) => field.color_name === colorName,
    );

    if (existingColor) {
      return;
    } else {
      append({
        color_name: colorName,
        hex_code: colorValue,
        size_list: [],
        color_id: colorId,
      });
    }
  };

  useEffect(() => {
    if (focusName !== name) {
      setOpen(false);
    }
  }, [focusName]);

  // 페이지를 벗어날 때 색상 선택 초기화
  useEffect(() => {
    return () => {
      setColor("");
      setColorId(0);
      setSelectedValue(t("선택"));
    };
  }, []);

  useEffect(() => {
    if (open && buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      setDropdownPos({
        top: rect.bottom + window.scrollY,
        left: rect.left,
        width: rect.width,
      });
    }
  }, [open]);

  return (
    <Container>
      <Button
        onClick={handleToggle}
        type="button"
        selectedValue={color === "" ? selectedValue : color}
        ref={buttonRef}>
        {color === "" ? selectedValue : color}
        <Image
          src={
            open
              ? theme.image.arrow_up_gray.default
              : theme.image.arrow_down_gray.default
          }
        />
      </Button>

      {open &&
        ReactDOM.createPortal(
          <SelectBox
            top={dropdownPos.top}
            left={dropdownPos.left}
            width={dropdownPos.width}
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.1 }}>
            {colorList?.map((item: any, index: number) => (
              <OptionItem
                type="button"
                key={item.id}
                last={index === colorList?.length - 1}
                onClick={() => handleChange(item.name, item.hex_code, item.id)}>
                <ColorBall color={item.hex_code} />
                {item.name}
              </OptionItem>
            ))}
          </SelectBox>,
          document.body,
        )}
    </Container>
  );
};

export default ColorDropdown;
